import Quill from 'quill';
import anchorLinkHandler from '@/components/texteditor/toolbar/anchorLinkHandler';
import advancedLinkHandler from '@/components/texteditor/toolbar/advancedLinkHandler';
import grammarCheckerHandler from '@/components/texteditor/toolbar/grammarCheckerHandler';
import searchReplaceHandler from '@/components/texteditor/toolbar/searchReplaceHandler';
import dividerHandler from '@/components/texteditor/toolbar/dividerHandler';
import fullscreenHandler from '@/components/texteditor/toolbar/fullscreenHandler';

const commons = {
  theme: 'snow',
  boundary: document.body,
  placeholder: '',
  readOnly: false,
  scrollingContainer: document.documentElement,
  history: {
    delay: 2000,
    maxStack: 500,
    userOnly: true,
  },
};

const basic = Object.assign({
  modules: {
    advancedLink: true,
    toolbar: {
      container: [
        ['bold', 'italic', 'strike', 'link'],
        [{ list: 'ordered' }, { list: 'bullet' }],
        ['clean'],
      ],
      handlers: {
        'advanced-link': advancedLinkHandler,
        'grammar-checker': grammarCheckerHandler,
        fullscreen: fullscreenHandler,
      },
    },
  },
}, commons);

// De acuerdo a https://mgr.bitban.com/issues/68869 existe la necesidad
// de evitar copiar el color de fondo y el color de un texto cuando se hace copy & paste
// He llegado a esta implementación a raíz de https://quilljs.com/docs/modules/clipboard/
// y https://github.com/quilljs/delta#map.
// Una alternativa a esto sería una implementación customizada del módulo de `clipboard` de Quill
// como se comenta en https://stackoverflow.com/a/51255601 y https://quilljs.com/docs/modules/#extending
// pero ese planteamiento creo que resulta más propenso a errores
function backgroundMatcher(node, delta) {
  const Delta = Quill.import('delta');
  const opts = delta.map((op) => {
    if (op.attributes && op.attributes.background) {
      delete op.attributes.background;
    }
    return op;
  });
  return new Delta(opts);
}

// Elimina el color del texto cuando se hace copy & paste de texto.
function colorMatcher(node, delta) {
  const Delta = Quill.import('delta');
  const opts = delta.map((op) => {
    if (op.attributes && op.attributes.color) {
      delete op.attributes.color;
    }
    return op;
  });
  return new Delta(opts);
}

const advanced = Object.assign({
  modules: {
    advancedLink: true,
    anchorLink: true,
    clipboard: {
      matchers: [
        ['[style*=background-color', backgroundMatcher],
        ['[style*=color', colorMatcher],
      ],
    },
    toolbar: {
      container: [
        [{ header: [1, 2, 3, 4, 5, 6, false] }],
        ['bold', 'italic', 'strike', 'link', 'advanced-link'],
        [{ list: 'ordered' }, { list: 'bullet' }],
        ['clean'],
        ['undo', 'redo'],
        ['grammar-checker'],
      ],
      handlers: {
        undo() {
          this.quill.history.undo();
        },
        redo() {
          this.quill.history.redo();
        },
        'search-replace': searchReplaceHandler,
        'anchor-link': anchorLinkHandler,
        'advanced-link': advancedLinkHandler,
        fullscreen: fullscreenHandler,
        divider: dividerHandler,
        'grammar-checker': grammarCheckerHandler,
      },
    },
    keyboard: {
      bindings: {
        'empty question-answer': {
          key: 13,
          collapsed: true,
          format: ['question-answer'],
          empty: true,
          handler(range, context) {
            this.quill.format('question-answer', false);
          },
        },
        'not empty question-answer': {
          key: 13,
          collapsed: true,
          format: ['question-answer'],
          empty: false,
          handler(range, context) {
            const formatTransform = {
              question: 'answer',
              answer: 'question',
            };
            const { 'question-answer': questionAnswerFormat } = this.quill.getFormat();

            this.quill.insertText(range.index, '\n');
            this.quill.format('question-answer', formatTransform[questionAnswerFormat]);
          },
        },
      },
    },
  },
}, commons);

const scrollytelling = Object.assign({
  modules: {
    advancedLink: true,
    anchorLink: true,
    clipboard: {
      matchers: [
        ['[style*=background-color', backgroundMatcher],
        ['[style*=color', colorMatcher],
      ],
    },
    toolbar: {
      container: [
        [{ header: [1, 2, 3, 4, false] }],
        [{ align: '' }, { align: 'center' }, { align: 'right' }],
        ['bold', 'italic', 'strike', 'link', 'advanced-link'],
        [{ list: 'ordered' }, { list: 'bullet' }],
        ['clean'],
        ['undo', 'redo'],
        ['divider'],
      ],
      handlers: {
        undo() {
          this.quill.history.undo();
        },
        redo() {
          this.quill.history.redo();
        },
        'advanced-link': advancedLinkHandler,
        divider: dividerHandler,
        'grammar-checker': grammarCheckerHandler,
      },
    },
  },
}, commons);

export default {
  basic,
  advanced,
  scrollytelling,
};
